import type { BadgeInfo } from './badgeInfo';
import { badges } from './badges';
import type { Signal } from './signal';
import type { UrgencySignals } from '../RelatedEvents/types';

/**
 * Converts urgency signals to tracking format
 */
export function getUrgencySignalsForTracking(urgencySignals?: UrgencySignals): string[] {
  if (!urgencySignals) {
    return [];
  }

  const signals: string[] = [];

  if (urgencySignals.new) signals.push('new');
  if (urgencySignals.salesEndSoon) signals.push('salesEndSoon');
  if (urgencySignals.fewTickets) signals.push('fewTickets');
  if (urgencySignals.popular) signals.push('popular');
  if (urgencySignals.goingFast) signals.push('goingFast');

  return signals;
}

/**
 * Filters and sorts active badges by priority (lower number = higher priority)
 */
export function getActiveBadgesSortedByPriority(urgencySignals: UrgencySignals): BadgeInfo[] {
  return badges
    .filter((badge) => urgencySignals[badge.signal as keyof UrgencySignals])
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Gets the highest priority badge to display a single one
 * Prioritizes 'message' type badges over 'category' type
 * Used in RelatedEventCard
 */
export function getHighestPriorityBadge(urgencySignals: UrgencySignals | undefined): BadgeInfo | null {
  if (!urgencySignals) return null;

  const activeBadges = getActiveBadgesSortedByPriority(urgencySignals);
  if (activeBadges.length === 0) return null;

  const messageBadge = activeBadges.find((badge) => badge.type === 'message');
  const categoryBadge = activeBadges.find((badge) => badge.type === 'category');

  return messageBadge || categoryBadge || null;
}

/**
 * Gets visible badges following EventSignalsBar rules:
 * - If there are 'message' badges: 1 message + maximum 1 category
 * - If only 'category' badges: maximum 2 category
 * Used in UrgencySignals component
 */
export function getVisibleBadges(urgencySignals: Signal[]): BadgeInfo[] {
  const presentBadges = badges
    .sort((a, b) => a.priority - b.priority)
    .filter((badge) => urgencySignals.includes(badge.signal));

  const messageBadge = presentBadges.filter((badge) => badge.type === 'message')[0];
  const categoryBadges = presentBadges.filter((badge) => badge.type === 'category');

  if (messageBadge) {
    return [messageBadge, ...categoryBadges.slice(0, 1)];
  }

  return categoryBadges.slice(0, 2);
}
